import React from "react";

export type RoomCardProps = {
  text: string;
  cardType?: string;
  Number?: string;
  Suit?: string;
  size?: "sm" | "md" | "lg";
  selected?: boolean;
  disabled?: boolean;
  visibility?: "faceUp" | "faceDown";
  onClick?: () => void;
};

const ACCENT: Record<string, string> = {
  standard:    "rgb(252,227,0)",
  free:        "rgb(252,227,0)",
  logical:     "rgb(1,174,214)",
  storytelling:"rgb(1,193,160)",
  life:        "rgb(224,82,62)",
  emotion:     "rgb(244,155,187)",
};

const NEUTRAL = "#d8d8d8";

function accentColor(cardType?: string): string {
  if (!cardType) return NEUTRAL;
  return ACCENT[cardType.toLowerCase()] ?? NEUTRAL;
}

/** Phrases that break unnaturally when wrapped — force single-line */
const FORCE_NOWRAP_WORDS = new Set([
  "言うまでもなく",
  "もしかすると",
  "いいかえると",
  "いつのまにか",
  "少なくとも",
]);

/** Phrases with explicit line-break positions to avoid unnatural breaks */
const FORCED_BREAKS: Record<string, string[]> = {
  "それはさておき": ["それは", "さておき"],
  "そういうわけで": ["そういう", "わけで"],
  "せっかくだから": ["せっかく", "だから"],
};

const normalizeText = (s: string) => (s ?? "").replace(/\s+/g, "").normalize("NFKC");

function formatCardText(text: string): React.ReactNode {
  const normalized = normalizeText(text);
  const lines = FORCED_BREAKS[normalized];
  if (lines) {
    return lines.map((line, i) => (
      <React.Fragment key={line + i}>
        {i > 0 && <br />}
        {line}
      </React.Fragment>
    ));
  }
  return text;
}

/** Pure kana/kanji text of 6 or fewer full-width characters, or a known phrase → no wrapping */
function isShortJP(s: string): boolean {
  const normalized = normalizeText(s);
  if (FORCE_NOWRAP_WORDS.has(normalized)) return true;
  return (
    /^[\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}ー・]+$/u.test(normalized) &&
    Array.from(normalized).length <= 6
  );
}

const SIZE = {
  sm: { width: 80,  height: 112, borderRadius: 8,  fontSize: 11, padding: "8px 6px"   },
  md: { width: 96,  height: 134, borderRadius: 10, fontSize: 12, padding: "8px 12px"  },
  lg: { width: 160, height: 224, borderRadius: 12, fontSize: 15, padding: "8px 12px"  },
};

function RoomCard({
  text,
  cardType,
  Number: cardNumber,
  Suit: cardSuit,
  size = "md",
  selected = false,
  disabled = false,
  visibility,
  onClick,
}: RoomCardProps) {
  const cfg = SIZE[size];

  const style: React.CSSProperties = {
    width: cfg.width,
    height: cfg.height,
    borderRadius: cfg.borderRadius,
    border: "1px solid #d1d5db",
    outline: selected ? "2px solid rgb(59,130,246)" : "none",
    outlineOffset: -3,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flexShrink: 0,
    opacity: disabled ? 0.5 : 1,
    transition: "outline-color 120ms ease, opacity 120ms ease",
    cursor: onClick ? (disabled ? "not-allowed" : "pointer") : "default",
    // button resets
    padding: 0,
    font: "inherit",
    appearance: "none" as React.CSSProperties["appearance"],
    textAlign: "left",
  };

  const isFaceDown = visibility === "faceDown";
  const nowrap = isShortJP(text);

  const showSuit = cardSuit && cardSuit !== "NONE";
  const suitColor = (cardSuit === "♥" || cardSuit === "♦") ? "#ef4444" : "#111";

  const inner = isFaceDown ? (
    <div style={{ flex: 1, padding: 6, display: "flex" }}>
      <div style={{
        flex: 1,
        background: accentColor(cardType),
        borderRadius: Math.max(cfg.borderRadius - 6, 3),
      }} />
    </div>
  ) : (
    <div style={{
      width: "100%",
      height: "100%",
      borderRadius: cfg.borderRadius,
      background: "#fff",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      padding: cfg.padding,
    }}>
      {/* Rank / Suit row — always reserve height when either field is present */}
      {(cardNumber || showSuit) && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          minHeight: cfg.fontSize * 1.2,
          fontWeight: "bold",
          fontSize: cfg.fontSize,
          lineHeight: 1,
          color: suitColor,
          flexShrink: 0,
        }}>
          <span>{cardNumber ?? ""}</span>
          {showSuit ? <span>{cardSuit}</span> : <span style={{ visibility: "hidden" }}>&nbsp;</span>}
        </div>
      )}
      {/* Card text — flex wrapper centers the block; inner div is block-level for text-wrap: balance */}
      <div style={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
        <p
          className={`ktr-card-text${nowrap ? " nowrap-short" : ""}`}
          style={{
            margin: 0,
            width: "100%",
            fontSize: cfg.fontSize,
            textAlign: "center",
            lineHeight: 1.2,
            color: "#111",
          }}
        >
          {formatCardText(text)}
        </p>
      </div>
    </div>
  );

  if (onClick) {
    return (
      <button onClick={disabled ? undefined : onClick} style={style}>
        {inner}
      </button>
    );
  }

  return <div style={style}>{inner}</div>;
}

export default React.memo(RoomCard);
